import { Portal } from '@finn/design-system/atoms/portal';
import { cn, useUniversalRouter } from '@finn/ui-utils';
import * as React from 'react';
import { PropsWithChildren, useEffect, useRef } from 'react';

type Props = {
  entryOffset: number;
};

const StickyPanelContainer: React.FC<PropsWithChildren<Props>> = ({
  entryOffset,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const thresholdRef = useRef<HTMLDivElement | null>(null);

  const { isReady } = useUniversalRouter();

  useEffect(() => {
    if (!isReady) {
      return;
    }

    setTimeout(() => {
      const container = containerRef.current;
      const threshold = thresholdRef.current;

      if (!container || !threshold) {
        return;
      }

      const observer = new IntersectionObserver((records) => {
        const record = records[0]!;

        if (!record.isIntersecting) {
          container.classList.add('translate-y-0');
        } else {
          container.classList.remove('translate-y-0');
        }
      });

      observer.observe(threshold);

      if (document.body.scrollTop >= entryOffset) {
        container.classList.add('translate-y-0');
      }

      return () => {
        observer.disconnect();
      };
    }, 100);

    // Should fire only once and only when `isReady` is `true`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  return (
    <>
      <div
        ref={containerRef}
        className={cn(
          'z-sticky shadow-highlight fixed left-0 right-0 top-0 -translate-y-full transition-all'
        )}
      >
        {children}
      </div>

      <Portal>
        <div
          ref={thresholdRef}
          className="absolute left-0 top-0"
          style={{ height: entryOffset }}
        />
      </Portal>
    </>
  );
};

export default React.memo(StickyPanelContainer);
